import { supabase } from '@/lib/supabaseClient';

/**
 * Returns total bytes used by a single tenant (from uploaded_files).
 * Treats NULL file_size_bytes as 0.
 */
export async function getStorageUsedBytes(tenantId: string): Promise<number> {
  const { data } = await supabase
    .from('uploaded_files')
    .select('file_size_bytes')
    .eq('tenant_id', tenantId);

  if (!data) return 0;
  return data.reduce((sum, row) => sum + (row.file_size_bytes ?? 0), 0);
}

/**
 * Returns a map of { [tenantId]: totalBytes } for all tenants.
 * One single query — efficient for SA views.
 */
export async function getAllTenantsStorageMap(): Promise<Record<string, number>> {
  const { data } = await supabase
    .from('uploaded_files')
    .select('tenant_id, file_size_bytes');

  if (!data) return {};

  return data.reduce((map, row) => {
    const prev = map[row.tenant_id] ?? 0;
    map[row.tenant_id] = prev + (row.file_size_bytes ?? 0);
    return map;
  }, {} as Record<string, number>);
}

/** bytes → MB (float) */
export function bytesToMb(bytes: number): number {
  return bytes / (1024 * 1024);
}

/** Human-readable size string */
export function formatStorageBytes(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${bytes} B`;
}

/** MB value → human-readable string */
export function formatStorageMb(mb: number): string {
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  return `${mb.toFixed(1)} MB`;
}
